import React, { useState, useMemo, useEffect } from 'react';
import { isNil } from 'ramda';

import useDialog from 'lib/hooks/useDialog';

import { useHistory, useLocation } from 'react-router-dom';

import CreatePatientDialog from './CreatePatientDialog';
import CreateSuccessDialog from './CreateSuccessDialog';
import AddPatientMethodDialog from 'components/insi/AddPatientMethodDialog';
import InsiSearchModal from 'components/insi/InsiSearchModal';
import { useInsiAccess } from 'services/insi';
import EmptyPage from './EmptyPage';
import LoadingPage from 'components/LoadingPage';
import AddCarePlanDialog from 'components/ui/AddCarePlanDialog/index';

import {
  useAddAppointmentDialog,
  usePageLoading,
  useSetFilters,
} from './hooks';
import { PatientsPageContainer } from './styled/patientsPageLayout';
import AppointmentDialog from '../SchedulePage/AppointmentDialog';
import ImportButton from './components/buttons/ImportButton';
import PatientsListBody from './PatientsListBody';
import useGetPatients from './hooks/useGetPatients';
import useCreatePatient from './hooks/useCreatePatient';
import ImportPatientsDialog from './ImportPatientsDialog';
// import { GET_PERMISSION_LIST } from 'pagesAdmin/SettingsPage/gqlSchemes/getPermissionList';

export const TableContext = React.createContext({});

function PatientsPage() {

  const createPatientDialog = useDialog();
  const createSuccessDialog = useDialog();
  const importPatientsDialog = useDialog();
  const addCarePlanDialog = useDialog();

  const history = useHistory();
  const location = useLocation();
  const hasInsiAccess = useInsiAccess();

  // INSi create-flow orchestration (T08):
  //  - method-choice dialog ("Search in INSi" vs "Create manually")
  //  - the standalone InsiSearchModal
  //  - the confirmed identity carried into CreatePatientDialog (INSi mode)
  const [isMethodDialogOpen, setIsMethodDialogOpen] = useState(false);
  const [isInsiSearchOpen, setIsInsiSearchOpen] = useState(false);
  const [insiIdentity, setInsiIdentity] = useState(null);

  const [pageNumber, setPageNumber] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(paginationInfo?.perPage || 5);

  const {
    appointmentDialog,
    onOpenAppointmentDialog,
  } = useAddAppointmentDialog();

  const {
    filters,
    setFilters,
    resetFilters,
    handleSearch,
    debouncedFilters,
    hasFiltersActive,
    getActiveFilterTypes,
  } = useSetFilters();

  // Open filter panel by default if there are active filters from URL
  const [isFiltersOpen, setIsFiltersOpen] = useState(() => hasFiltersActive());

  useEffect(() => {
    setPageNumber(0);
  }, [filters]);

  const onSearch = (event) => {
    handleSearch(event.target.value);
  };

  const { patients, loading } = useGetPatients({
    filter: debouncedFilters,
    perPage: rowsPerPage,
    page: pageNumber,
    setPageNumber,
    setPaginationInfo,
  });

  const { createPatient } = useCreatePatient(
    openNewPatientDialog,
    createSuccessDialog
  );

  const pageLoading = usePageLoading(loading);

  // Entry point for every "Add patient" button. When INSi is available the PS
  // first chooses a creation method; otherwise we go straight to manual create.
  function openNewPatientDialog() {
    setInsiIdentity(null);
    if (hasInsiAccess) {
      setIsMethodDialogOpen(true);
    } else {
      openManualCreateDialog();
    }
  }

  async function openManualCreateDialog() {
    setInsiIdentity(null);
    await createPatientDialog.open({ createPatient });
  }

  async function openInsiCreateDialog(identity) {
    setInsiIdentity(identity);
    await createPatientDialog.open({ createPatient });
  }

  const handleMethodSelect = (method) => {
    setIsMethodDialogOpen(false);
    if (method === 'insi') {
      setIsInsiSearchOpen(true);
    } else {
      openManualCreateDialog();
    }
  };

  // InsiSearchModal callbacks.
  const handleInsiConfirm = (insIdentifier, traits) => {
    setIsInsiSearchOpen(false);
    openInsiCreateDialog({ insIdentifier, traits });
  };

  const handleInsiCreateProvisional = () => {
    setIsInsiSearchOpen(false);
    openManualCreateDialog();
  };

  const handleInsiLinkExisting = (patientId) => {
    setIsInsiSearchOpen(false);
    history.push(`/patientRecord/${patientId}`, {
      from: {
        pathname: location.pathname,
        search: location.search,
        fullUrl: location.pathname + location.search,
        title: 'Patients',
      },
    });
  };

  // "Update INSi Data" on the locked-identity card: close the create form and
  // re-open the search to replace the selection.
  const handleUpdateInsi = () => {
    createPatientDialog.close();
    setInsiIdentity(null);
    setIsInsiSearchOpen(true);
  };

  const valueForTableContext = useMemo(
    () => ({
      patients,
      paginationInfo,
      setPageNumber,
      pageNumber,
      rowsPerPage,
      setRowsPerPage,
      loading,
      isFiltersOpen,
      filters,
      setFilters,
      onOpenAppointmentDialog,
      onAddCarePlan: (patient) => addCarePlanDialog.open({ mode: 'patient', patient }),
    }),
    [
      patients,
      paginationInfo,
      setPageNumber,
      pageNumber,
      rowsPerPage,
      setRowsPerPage,
      isFiltersOpen,
      loading,
      filters,
      setFilters,
      onOpenAppointmentDialog,
      addCarePlanDialog,
    ]
  );

  return (
    <div>
      {isNil(patients) ? (
        loading ? (
          <LoadingPage />
        ) : (
          <EmptyPage openNewPatientDialog={openNewPatientDialog} />
        )
      ) : pageLoading ? (
        <LoadingPage />
      ) : (
        <PatientsPageContainer isOpen={isFiltersOpen}>
          <PatientsListBody
            filters={filters}
            isFiltersOpen={isFiltersOpen}
            setFilters={setFilters}
            patients={patients}
            valueForTableContext={valueForTableContext}
            resetFilters={resetFilters}
            activeFilterTypes={getActiveFilterTypes()}
            filterName={filters.name || ''}
            onFilterButtonClick={setIsFiltersOpen}
            onSearch={onSearch}
            openNewPatientDialog={openNewPatientDialog}
            openImportPaitentDialog={importPatientsDialog.open}
          />
        </PatientsPageContainer>
      )}
      {isMethodDialogOpen && (
        <AddPatientMethodDialog
          open={isMethodDialogOpen}
          onClose={() => setIsMethodDialogOpen(false)}
          onSelect={handleMethodSelect}
        />
      )}
      {isInsiSearchOpen && (
        <InsiSearchModal
          open={isInsiSearchOpen}
          onClose={() => setIsInsiSearchOpen(false)}
          initialTraits={
            insiIdentity
              ? {
                  nomNaissance: insiIdentity.traits?.nomNaissance,
                  // The modal expects a `prenoms` array (index 0 = first usual
                  // prénom), not a space-joined string.
                  prenoms: insiIdentity.traits?.listePrenom || [],
                  sexe: insiIdentity.traits?.sexe,
                  dateOfBirth: insiIdentity.traits?.dateNaissance,
                }
              : undefined
          }
          onConfirm={handleInsiConfirm}
          onCreateProvisional={handleInsiCreateProvisional}
          onLinkExisting={handleInsiLinkExisting}
        />
      )}
      {createPatientDialog.isOpen && (
        <CreatePatientDialog
          isOpen={createPatientDialog.isOpen}
          close={createPatientDialog.close}
          initialData={createPatientDialog.initialData}
          insiIdentity={insiIdentity}
          onUpdateInsi={handleUpdateInsi}
        />
      )}
      {addCarePlanDialog.isOpen && (
        <AddCarePlanDialog 
          isOpen={addCarePlanDialog.isOpen}
          close={addCarePlanDialog.close}
          mode={addCarePlanDialog.initialData?.mode}
          patient={addCarePlanDialog.initialData?.patient}
          patientGroup={addCarePlanDialog.initialData?.patientGroup}
        />
      )}
      {appointmentDialog.isOpen && (
        <AppointmentDialog
          isOpen={appointmentDialog.isOpen}
          close={appointmentDialog.close}
          initialData={appointmentDialog.initialData}
        />
      )}
      {createSuccessDialog.isOpen && (
        <CreateSuccessDialog
          isOpen={createSuccessDialog.isOpen}
          close={createSuccessDialog.close}
          initialData={createSuccessDialog.initialData}
        />
      )}
      {importPatientsDialog.isOpen && (
        <ImportPatientsDialog
          isOpen={importPatientsDialog.isOpen}
          close={importPatientsDialog.close}
          initialData={importPatientsDialog.initialData}
        />
      )}
    </div>
  );
}

export default PatientsPage;
